/**
 * ChatWidget — floating bubble (bottom-right) + popup panel.
 */

import React, { useState, useCallback, useRef, useEffect } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useChatContext } from "../../context/ChatContext";
import ChatWindow from "./ChatWindow";

let _msgId = 0;
const nextId = () => `m${++_msgId}`;

const GREETING = {
  id: nextId(),
  role: "bot",
  content:
    "Hi! I'm Growfin AI — your platform support assistant.\n\n" +
    "I can help you understand strategies, build rules, navigate the platform, " +
    "and troubleshoot issues. What would you like to know?",
  ts: new Date(),
};

export default function ChatWidget() {
  const history  = useHistory();
  const { user } = useAuth();
  const { pendingMessage, clearPendingMessage } = useChatContext();
  const [open, setOpen]             = useState(false);
  const [messages, setMessages]     = useState([GREETING]);
  const [loading, setLoading]       = useState(false);
  const [hasUnread, setHasUnread]   = useState(false);
  const [initialDraft, setInitialDraft] = useState("");
  const sessionId = useRef(null);

  /* create a backend session on mount */
  useEffect(() => {
    const username = user?.username || "anonymous";
    axios.post("/chat/session", { username })
      .then(res => { sessionId.current = res.data.session_id; })
      .catch(() => { sessionId.current = `widget-${Date.now()}`; });
  }, [user]);

  /* open widget and pre-fill message when a help bubble is clicked */
  useEffect(() => {
    if (pendingMessage) {
      setInitialDraft(pendingMessage);
      setOpen(true);
      setHasUnread(false);
      clearPendingMessage();
    }
  }, [pendingMessage, clearPendingMessage]);

  /* close popup and navigate to full page */
  const openFullPage = useCallback(() => {
    setOpen(false);
    history.push("/chat");
  }, [history]);

  /* toggle bubble */
  const toggleOpen = () => {
    setOpen(prev => {
      if (!prev) setHasUnread(false); // clear badge on open
      return !prev;
    });
  };

  /* send message */
  const handleSend = useCallback(async (text) => {
    const userMsg = { id: nextId(), role: "user", content: text, ts: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      const res = await axios.post("/chat/message", {
        session_id: sessionId.current,
        message:    text,
        username:   user?.username || "anonymous",
      });

      const data = res.data;
      const botMsg = {
        id:        nextId(),
        role:      "bot",
        content:   data.reply,
        ts:        new Date(),
        escalated: data.escalated,
        confidence: data.confidence,
      };
      setMessages(prev => [...prev, botMsg]);

      if (data.token_warning) {
        setMessages(prev => [...prev, {
          id: nextId(), role: "system",
          content: "⚠️ This conversation is approaching its message limit. Consider starting a new chat soon.",
          ts: new Date(),
        }]);
      }
    } catch (err) {
      const errMsg = {
        id: nextId(), role: "bot",
        content: err.response?.data?.reply || "⚠️ Something went wrong. Please try again in a moment.",
        ts: new Date(),
      };
      setMessages(prev => [...prev, errMsg]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  return (
    <>
      {/* ── popup panel ──────────────────────────────────────────── */}
      {open && (
        <div style={{
          position: "fixed",
          bottom: 84,
          right: 24,
          width: 360,
          height: 520,
          borderRadius: 16,
          boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
          background: "#fff",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          zIndex: 9999,
          animation: "chat-popup-in 0.2s ease-out",
        }}>
          {/* header */}
          <div style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            padding: "14px 16px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexShrink: 0,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: "50%",
                background: "rgba(255,255,255,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 18,
              }}>
                🤖
              </div>
              <div>
                <div style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>
                  Growfin AI
                </div>
                <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 11 }}>
                  Platform support assistant
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <IconButton
                title="Open full chat"
                onClick={openFullPage}
                style={{ color: "rgba(255,255,255,0.85)" }}
              >
                <ExpandIcon />
              </IconButton>
              <IconButton
                title="Close"
                onClick={() => setOpen(false)}
                style={{ color: "rgba(255,255,255,0.85)" }}
              >
                <CloseIcon />
              </IconButton>
            </div>
          </div>

          {/* chat window */}
          <div style={{ flex: 1, overflow: "hidden" }}>
            <ChatWindow
              messages={messages}
              onSend={(text) => { setInitialDraft(""); handleSend(text); }}
              loading={loading}
              compact={true}
              onOpenFullPage={openFullPage}
              sessionId={sessionId.current}
              hasUnread={hasUnread}
              initialDraft={initialDraft}
            />
          </div>
        </div>
      )}

      {/* ── floating bubble ───────────────────────────────────────── */}
      <button
        onClick={toggleOpen}
        title="Growfin AI Support"
        style={{
          position: "fixed",
          bottom: 24,
          right: 24,
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: open
            ? "#555"
            : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 4px 20px rgba(102,126,234,0.5)",
          zIndex: 10000,
          transition: "transform 0.2s, background 0.2s",
          transform: open ? "rotate(45deg)" : "rotate(0deg)",
        }}
        onMouseEnter={e => !open && (e.currentTarget.style.transform = "scale(1.1)")}
        onMouseLeave={e => !open && (e.currentTarget.style.transform = "scale(1)")}
      >
        {open ? <CloseIcon color="#fff" size={22} /> : <ChatIcon size={24} />}

        {/* unread badge */}
        {hasUnread && !open && (
          <span style={{
            position: "absolute",
            top: 2, right: 2,
            width: 14, height: 14,
            borderRadius: "50%",
            background: "#ff4757",
            border: "2px solid #fff",
            display: "block",
          }} />
        )}
      </button>

      <style>{`
        @keyframes chat-popup-in {
          from { opacity: 0; transform: translateY(16px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </>
  );
}

/* ── small icon button ───────────────────────────────────────────────── */
function IconButton({ children, onClick, title, style = {} }) {
  return (
    <button
      title={title}
      onClick={onClick}
      style={{
        background: "none", border: "none", cursor: "pointer",
        padding: 4, borderRadius: 6,
        display: "flex", alignItems: "center", justifyContent: "center",
        opacity: 0.85, transition: "opacity 0.15s",
        ...style,
      }}
      onMouseEnter={e => e.currentTarget.style.opacity = "1"}
      onMouseLeave={e => e.currentTarget.style.opacity = "0.85"}
    >
      {children}
    </button>
  );
}

/* ── icons ───────────────────────────────────────────────────────────── */
function ChatIcon({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
        stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        fill="rgba(255,255,255,0.15)"
      />
    </svg>
  );
}

function CloseIcon({ size = 18, color = "white" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M18 6L6 18M6 6l12 12" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
    </svg>
  );
}

function ExpandIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"
        stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
